import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Share2, Copy, Check, Ghost, Image, Gift, Lock } from "lucide-react";
import { toast } from "sonner";
import memberApi from "../../lib/memberApi";

export const ShareProgress = ({ completed = 0, total = 0 }) => {
  const [copied, setCopied] = useState(false);
  const [refCopied, setRefCopied] = useState(false);
  const [referral, setReferral] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    memberApi.get("/member/referral").then((r) => setReferral(r.data)).catch(() => {});
  }, [completed]);

  const shareUrl = referral?.url || "https://ghostthegrid.com";
  const text = `I ghosted ${completed} of ${total} nodes on Ghost The Grid, taking my privacy back one setting at a time.`;
  const shareText = `${text} ${shareUrl}`;

  const copyTo = async (value, setter) => {
    try {
      await navigator.clipboard.writeText(value);
      setter(true);
      toast.success("Copied");
      setTimeout(() => setter(false), 2000);
    } catch { toast.error("Couldn't copy"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Ghost The Grid", text, url: shareUrl }); } catch { /* cancelled */ }
    } else { copyTo(shareText, setCopied); }
  };

  const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;

  // Draw an on-brand 1200x630 badge entirely with canvas primitives (no cross-origin images -> exportable).
  const makeBadge = async () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, 1200, 630);
    // subtle grid
    ctx.strokeStyle = "rgba(255,255,255,0.05)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= 1200; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, 630); ctx.stroke(); }
    for (let y = 0; y <= 630; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(1200, y); ctx.stroke(); }
    // accent frame
    ctx.strokeStyle = "#ff2a2a"; ctx.lineWidth = 4; ctx.strokeRect(40, 40, 1120, 550);
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#ff2a2a"; ctx.font = "700 22px monospace";
    ctx.fillText("// GHOST THE GRID", 80, 120);
    ctx.fillStyle = "#f8f9fa"; ctx.font = "800 64px Arial";
    ctx.fillText("I GHOSTED", 80, 250);
    ctx.fillStyle = "#ff2a2a"; ctx.font = "800 150px Arial";
    ctx.fillText(`${completed}`, 80, 420);
    const w = ctx.measureText(`${completed}`).width;
    ctx.fillStyle = "#f8f9fa"; ctx.font = "800 90px Arial";
    ctx.fillText(`/ ${total} NODES`, 80 + w + 30, 420);
    // progress bar
    const pct = total ? completed / total : 0;
    ctx.fillStyle = "rgba(255,255,255,0.12)"; ctx.fillRect(80, 470, 1040, 16);
    ctx.fillStyle = "#ff2a2a"; ctx.fillRect(80, 470, 1040 * pct, 16);
    ctx.fillStyle = "#9a9a9a"; ctx.font = "400 24px Arial";
    ctx.fillText("You are the firewall.  ghostthegrid.com", 80, 545);
    return await new Promise((res) => c.toBlob(res, "image/png"));
  };

  const shareImage = async () => {
    try {
      const blob = await makeBadge();
      const file = new File([blob], "ghosted-badge.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "ghosted-badge.png";
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Badge image downloaded");
      }
    } catch { toast.error("Couldn't make the image"); }
  };

  const referrals = referral?.referrals ?? 0;
  const allyUnlocked = referral?.ally_unlocked;

  return (
    <div data-testid="share-progress" className="border border-white/10 bg-[#0c0c0c] p-6 sm:p-7">
      <canvas ref={canvasRef} width={1200} height={630} className="hidden" />
      <div className="flex items-center gap-2 mb-3">
        <Ghost size={16} className="text-accent" />
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Share your mission</span>
      </div>
      <p className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl mb-1">
        I ghosted <span className="text-accent">{completed}</span> of {total} nodes
      </p>
      <p className="font-body text-sm text-white/50 leading-relaxed mb-5">
        Nudge someone you care about to take their privacy back too.
      </p>
      <div className="flex flex-wrap gap-3 mb-6">
        <button data-testid="share-native-button" onClick={nativeShare}
          className="inline-flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors">
          <Share2 size={14} /> Share
        </button>
        <button data-testid="share-image-button" onClick={shareImage}
          className="inline-flex items-center gap-2 border border-white/15 text-white/80 font-mono uppercase tracking-[0.15em] text-xs px-5 py-3 hover:border-accent hover:text-white transition-colors">
          <Image size={14} /> Image badge
        </button>
        <a data-testid="share-x-link" href={tweet} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-white/15 text-white/80 font-mono uppercase tracking-[0.15em] text-xs px-5 py-3 hover:border-accent hover:text-white transition-colors">
          Post on X
        </a>
      </div>

      {/* Referral link + perk */}
      <div className="border-t border-white/10 pt-5">
        <div className="flex items-center gap-2 mb-2">
          <Gift size={14} className="text-accent" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Invite a friend, unlock a bonus</span>
        </div>
        <p className="font-body text-sm text-white/50 leading-relaxed mb-3">
          When a friend joins through your link, you unlock the <strong className="text-white/80">Ghost Ally Toolkit</strong>, an advanced maintenance kit.
        </p>
        <div className="flex items-stretch gap-2 mb-3">
          <input
            data-testid="referral-link-input"
            readOnly
            value={referral?.url || ""}
            className="flex-1 bg-black/40 border border-white/15 px-3 py-2.5 font-mono text-[11px] text-white/70 focus:outline-none focus:border-accent truncate"
          />
          <button data-testid="referral-copy-button" onClick={() => copyTo(referral?.url || "", setRefCopied)}
            className="inline-flex items-center gap-2 border border-white/15 text-white/80 font-mono uppercase tracking-[0.15em] text-xs px-4 hover:border-accent hover:text-white transition-colors">
            {refCopied ? <Check size={14} className="text-accent" /> : <Copy size={14} />}
          </button>
        </div>
        <div className="flex items-center justify-between">
          <span data-testid="referral-count" className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">
            {referrals} friend{referrals === 1 ? "" : "s"} joined
          </span>
          {allyUnlocked ? (
            <Link data-testid="ally-link" to="/portal/ally" className="font-mono text-[11px] uppercase tracking-[0.15em] text-accent hover:text-white transition-colors">
              Open Ghost Ally Toolkit →
            </Link>
          ) : (
            <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white/30">
              <Lock size={12} /> Bonus locked
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
